import { useState, useEffect, ReactNode } from 'react';
import { useMediaQuery, useTheme } from '@material-ui/core';
import FilterModal from '../FilterModal';
import ExperienceScopeSelector, { ExperienceScope } from './ExperienceScopeSelector';
import ExperienceOperatorToggle from './ExperienceOperatorToggle';
import MinHoursSelector, { countAtLevel, stickyControls } from './MinHoursSelector';

export interface FacetOption {
  key: string;
  label: string;
  count: number;
  profilePicture?: string;
  countryCode?: string;
  [extra: string]: any;
}

interface Props {
  visible: boolean;
  onClose: () => void;
  onSave: (keys: string[], labels: string[], scope: ExperienceScope, operator: 'and' | 'or', minHours: number) => void;
  options: FacetOption[];
  initialSelected: string[];
  initialScope?: ExperienceScope;
  initialOperator?: 'and' | 'or';
  /** Min. hours of the applied filter; 0 = any */
  initialMinHours?: number;
  onScopeChange?: (scope: ExperienceScope) => void;
  title: string;
  description: string;
  color: string;
  searchPlaceholder?: string;
  emptyLabel?: string;
  /** Custom label renderer — receives the option, returns content placed after the checkbox */
  renderLabel?: (option: FacetOption) => ReactNode;
  /** Custom search filter — if provided, overrides default label search */
  searchFilter?: (option: FacetOption, query: string) => boolean;
}

const ExperienceFacetModal = ({
  visible, onClose, onSave, options, initialSelected,
  initialScope = 'allTime', initialOperator = 'and', initialMinHours = 0, onScopeChange,
  title, description, color, searchPlaceholder = 'Search...', emptyLabel = 'No items selected',
  renderLabel, searchFilter,
}: Props) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [selected, setSelected] = useState<string[]>(initialSelected);
  const [search, setSearch] = useState('');
  const [scope, setScope] = useState<ExperienceScope>(initialScope);
  const [operator, setOperator] = useState<'and' | 'or'>(initialOperator);
  const [minHours, setMinHours] = useState(initialMinHours);

  useEffect(() => {
    if (visible) {
      setSelected(initialSelected);
      setSearch('');
      setMinHours(initialMinHours);
    }
  }, [visible]);

  const sortedOptions = [...options].sort((a, b) => {
    const aApplied = initialSelected.includes(a.key);
    const bApplied = initialSelected.includes(b.key);
    if (aApplied && !bApplied) return -1;
    if (!aApplied && bApplied) return 1;
    return countAtLevel(b, minHours) - countAtLevel(a, minHours);
  });

  const toggle = (key: string) => {
    setSelected(prev =>
      prev.includes(key) ? prev.filter(k => k !== key) : [...prev, key]
    );
  };

  const matchesSearch = (option: FacetOption) => {
    if (!search) return true;
    if (searchFilter) return searchFilter(option, search);
    return option.label.toLowerCase().includes(search.toLowerCase());
  };

  const filteredOptions = sortedOptions
    .filter(o => selected.includes(o.key) || (matchesSearch(o) && countAtLevel(o, minHours) > 0));

  const handleScopeChange = (s: ExperienceScope) => {
    setScope(s);
    onScopeChange?.(s);
  };

  const handleSave = () => {
    const labels = selected.map(k => options.find(o => o.key === k)?.label || k);
    onSave(selected, labels, scope, operator, minHours);
    onClose();
  };

  return (
    <FilterModal
      visible={visible}
      onClose={onClose}
      onSave={handleSave}
      title={title}
      description={description}
    >
      <div>
        <div style={stickyControls(isMobile)}>
        <ExperienceScopeSelector value={scope} onChange={handleScopeChange} />
        <MinHoursSelector
          value={minHours}
          onChange={setMinHours}
          counts={selected.length === 1 ? options.find(o => o.key === selected[0]) : undefined}
        />
        <ExperienceOperatorToggle value={operator} onChange={setOperator} selectedCount={selected.length} />
        {options.length > 5 && (
          <input
            autoFocus={!isMobile}
            type="text"
            placeholder={searchPlaceholder}
            value={search}
            onChange={e => setSearch(e.target.value)}
            style={{
              width: '100%', padding: '8px 12px', borderRadius: 8,
              border: '1px solid #e0e0e0', fontSize: 13, marginBottom: 8,
              outline: 'none', boxSizing: 'border-box',
            }}
          />
        )}
        </div>

        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8, minHeight: 18 }}>
          {selected.length > 0 ? (
            <>
              <span style={{ fontSize: 12, color: '#999' }}>{selected.length} selected</span>
              <button
                onClick={() => setSelected([])}
                style={{ fontSize: 12, color, background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}
              >
                Clear all
              </button>
            </>
          ) : (
            <span style={{ fontSize: 12, color: '#ccc' }}>{emptyLabel}</span>
          )}
        </div>

        {filteredOptions.map(option => {
          const isSelected = selected.includes(option.key);
          const selectedBg = `${color}18`;
          return (
            <div
              key={option.key}
              onClick={() => toggle(option.key)}
              style={{
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                padding: '6px 12px', borderRadius: 6, cursor: 'pointer',
                backgroundColor: isSelected ? selectedBg : 'transparent',
                userSelect: 'none', WebkitUserSelect: 'none' as any,
                transition: 'background-color 0.1s',
                marginBottom: 1,
              }}
              onMouseEnter={e => { if (!isSelected) e.currentTarget.style.backgroundColor = '#FAFAFA'; }}
              onMouseLeave={e => { if (!isSelected) e.currentTarget.style.backgroundColor = 'transparent'; }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <div style={{
                  width: 18, height: 18, borderRadius: 4,
                  border: isSelected ? 'none' : '2px solid #ccc',
                  backgroundColor: isSelected ? color : 'white',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: 12, color: 'white', fontWeight: 700, flexShrink: 0,
                }}>
                  {isSelected && '✓'}
                </div>
                {renderLabel ? renderLabel(option) : (
                  <span style={{ fontSize: 14, color: '#333' }}>{option.label}</span>
                )}
              </div>
              <span style={{ fontSize: 12, color: '#999' }}>{countAtLevel(option, minHours)}</span>
            </div>
          );
        })}

        {filteredOptions.length === 0 && (
          <div style={{ padding: 20, textAlign: 'center', color: '#999', fontSize: 13 }}>
            {search ? 'No results match your search' : 'No data available'}
          </div>
        )}
      </div>
    </FilterModal>
  );
};

export default ExperienceFacetModal;
